import { isFulfilled } from "@reduxjs/toolkit";
import { FC, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ExpiryAlertMsg } from "../../../constants/app";
import { useImpersonation } from "../../../contexts/ImpersonationContext";
import { useReopenedStages } from "../../../hooks/useReopenedStages";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../store";
import { getProfileDetailsThunk, startReferroChecksThunk } from "../../../store/onboarding/thunk";
import { deleteCandidateDocumentThunk, updateDocumentDateThunk } from "../../../store/uploads/thunks";
import { getDateAfterYears, getMinDateByDocumentType, getValidityYearsByType, isOneMonthBefore } from "../../../utils/documentHelper";
import { isSuperAdmin } from "../../../utils/roleHelper";
import CustomButton from "../../UI/Buttons/CustomButton";
import ExternalLinkIcon from "../../UI/Icons/ExternalLinkIcon";
import RightArrow from "../../UI/Icons/RightArrow";
import Warning from "../../UI/Icons/Warning";
import CustomDateInput from "../../UI/Inputs/DateInput";
import DocumentUpload from "../../UI/Inputs/DocumentUpload";
import RadioInput from "../../UI/Inputs/RadioInput";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

const parseApiErrorMessage = (raw: string | undefined): string => {
  if (!raw) return 'An error occurred. Please try again.';
  try {
    // Extract the first JSON object embedded in the message string
    const jsonMatch = raw.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      const parsed = JSON.parse(jsonMatch[0]);
      // Flatten all string arrays under the `error` key
      if (parsed?.error && typeof parsed.error === 'object') {
        const messages = Object.values(parsed.error)
          .flat()
          .filter((v): v is string => typeof v === 'string');
        if (messages.length > 0) return messages.join(' ');
      }
    }
  } catch {
    // JSON parsing failed — fall through to fallback
  }
  return 'An error occurred. Please try again.';
};

const ComplainceCheck: FC = () => {
    const dispatch = useDispatch()

    const [showMessage, setShowMessage] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [hasPoliceCheck, setHasPoliceCheck] = useState<string>("")
    const [policeCheckError, setPoliceCheckError] = useState<string>("")
    const [policeCheckUploaded, setPoliceCheckUploaded] = useState<boolean>(false)
    const [policeCheckIssueDate, setPoliceCheckIssueDate] = useState<Date | null>(null)

    const Inprogress = useSelector((state) => state.onboarding.inProgress)
    const uploadsInProgress = useSelector((state) => state.uploads.inProgress)
    const userpersonaldetails = useSelector((state) => state.onboarding.userpersonaldetails)
    const qualificationDocs = useSelector((state) => state.onboarding.qualifications?.documents)
    const user = useSelector((state) => state.auth.user);
    const isAdmin = isSuperAdmin(user);
    const { isImpersonating, impersonationData } = useImpersonation();
    const { isSectionReopenedFn, reopenedSections } = useReopenedStages();
    const isCandidateCompleteReopened = isSectionReopenedFn('candidate_complete'); // Check if the candidate completion section is reopened for stage 2
    const isAdminCompleteOptionSelected = userpersonaldetails?.profileSteps?.some(step => step.step_number === 1 && step.action === "approve_candidate_bypass_compliance_check");


    const existingPoliceCheckDoc = qualificationDocs?.find(doc => doc.document_type === "police_check")
    const hasPoliceCheckDoc = policeCheckUploaded || !!existingPoliceCheckDoc
    const existingIssueDate = existingPoliceCheckDoc?.issued_date
      ? new Date(existingPoliceCheckDoc.issued_date)
      : null
    const resolvedIssueDate = policeCheckIssueDate ?? existingIssueDate

    const policeCheckExpiryDate = resolvedIssueDate
      ? getDateAfterYears(resolvedIssueDate.toISOString().slice(0, 10), getValidityYearsByType('police_check'))
      : null;
    const policeCheckGoingToExpire = policeCheckExpiryDate ? isOneMonthBefore(policeCheckExpiryDate) : false;

    const handlePoliceCheckDocDate = (date: Date | null) => {
      if (!date) return
      setPoliceCheckIssueDate(date)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      dispatch(updateDocumentDateThunk({ document_type: "police_check", date: `${year}-${month}-${day}`, candidate_uuid: userpersonaldetails?.uuid }))
    }

    const handleStartCheck = async () => {
      if (!hasPoliceCheck) {
        setPoliceCheckError("Please select an option to continue.")
        return
      }
      if (hasPoliceCheck === "yes" && !hasPoliceCheckDoc) {
        setPoliceCheckError("Please upload your police check certificate to continue.")
        return
      }
      if (hasPoliceCheck === "yes" && !resolvedIssueDate) {
        setPoliceCheckError("Please select the issue date for your police check certificate.")
        return
      }
      setPoliceCheckError("")
      setErrorMessage(null)
      const result = await dispatch(startReferroChecksThunk({ isReopen: isCandidateCompleteReopened, hasPoliceCheck: hasPoliceCheck === "yes" }))
      if(isFulfilled(result)){
        await dispatch(getProfileDetailsThunk())
        setShowMessage(true)
      } else {
        const payload = result.payload as { status?: string; message?: string } | undefined
        setErrorMessage(parseApiErrorMessage(payload?.message))
      }
    }

    const handleOpenReferooLink = () => {
      const referooLink = userpersonaldetails?.referro_data?.dashboard_url;
      if (referooLink) {
        window.open(referooLink, "_blank");
      }
    }

console.log("isCandidateCompleteReopened", isCandidateCompleteReopened, isAdminCompleteOptionSelected, showMessage)
    return (
      
        <div className="d-flex flex-column gap-25">
          <div className="d-flex flex-column gap-25 max-w-650px online_interview_container mobile-mt-0">
            <div className="fs-40 fw-400 text-black">Compliance Checks</div>
            { isImpersonating && 
                <div className={`bg-teal-light px-15px py-10px radius-8px d-flex gap-6`}>
                  <Warning />
                  <div className="fs-13 fw-400 text-black lh-sm">
                    You are currently impersonating a candidate. You cannot start the compliance check while in impersonation mode.
                  </div>
                </div>
              }
            {userpersonaldetails?.referoo_status === "Not initiated" || (isCandidateCompleteReopened && userpersonaldetails?.referoo_status !== "pending") ? (
              <>
                <div className="d-flex flex-column gap-25">
                  <div className="d-flex flex-column gap-25">
                    <div className="fs-16 fw-400 text-black">
                      <div className="mb-3">Your Stage 2 requirements include:</div>
                      <ul className="mb-0">
                        <li className="fs-16 fw-400 text-black">Police check</li>
                        <li className="fs-16 fw-400 text-black">Working With Children check</li>
                        <li className="fs-16 fw-400 text-black">Right To Work check</li>
                        <li className="fs-16 fw-400 text-black">2 reference checks</li>
                      </ul>
                    </div>
                    <div className="fs-16 fw-400 text-black">
                      This is done through our trusted partner, Referoo.
                    </div>

                    <div className="d-flex flex-column gap-10">
                        <div className="fs-16 fw-400 text-black">
                          Do you have a valid police check certificate?
                        </div>
                        <div className="d-flex align-items-center gap-20 theme-check">
                          <RadioInput
                            checked={hasPoliceCheck === "yes"}
                            label={"Yes"}
                            onChange={setHasPoliceCheck}
                            value={"yes"}
                            name="policeCheck"
                            themeCheck={true}
                          />
                          <RadioInput
                            checked={hasPoliceCheck === "no"}
                            label={"No"}
                            onChange={(val) => {
                              setHasPoliceCheck(val)
                              setPoliceCheckUploaded(false)
                              setPoliceCheckIssueDate(null)
                              setPoliceCheckError("")
                              if (hasPoliceCheckDoc) {
                                dispatch(deleteCandidateDocumentThunk({ document_type: "police_check" }))
                              }
                            }}
                            value={"no"}
                            name="policeCheck"
                            themeCheck={true}
                          />
                        </div>
                        {hasPoliceCheck === "yes" && (
                          <div className="d-flex flex-column gap-8 mt-1">
                            <div className="fs-16 fw-400 text-black">
                              Please upload your police check certificate:
                            </div>
                            <DocumentUpload
                              type="police_check"
                              required={true}
                              onFileSelect={(_file, _fileName, deleteFile) => {
                                setPoliceCheckUploaded(!deleteFile)
                                if (!deleteFile) setPoliceCheckError("")
                              }}
                            />
                            <div className="d-flex flex-column flex-md-row gap-10 align-items-md-center">
                              <div className="fs-13 fw-400 text-grey-dark text-nowrap">Issue date:</div>
                                <div className="min-w-200px">
                                  <DatePicker
                                    selected={hasPoliceCheckDoc ? resolvedIssueDate : null}
                                    onChange={handlePoliceCheckDocDate}
                                    customInput={<CustomDateInput placeholder="Select a date..." />}
                                    dateFormat="dd MMM yyyy"
                                    minDate={getMinDateByDocumentType("police_check")}
                                    maxDate={new Date()}
                                    disabled={!hasPoliceCheckDoc}
                                    showYearDropdown
                                    scrollableYearDropdown
                                    yearDropdownItemNumber={15}
                                  />
                                </div>
                              </div>
                              {policeCheckGoingToExpire && (
                                <ErrorMessage className="align-items-center gap-3 lh-1" message={ExpiryAlertMsg} />
                              )}
                            
                          </div>
                        )}
                        {policeCheckError && (
                          <div className="fs-13 text-danger">{policeCheckError}</div>
                        )}
                    </div>
                    <div className="fs-16 fw-400 text-black">
                      Please begin by clicking 'Start Checks' below.
                    </div>
                  </div>

                  

                  {/* <div className="fs-16 fw-400 text-black">
                    These audits need to be completed on Referoo.
                  </div>
                  <div className="fs-16 fw-400 text-black">
                    Please click 'Start Check' to begin the compliance check
                    process.
                  </div> */}
                </div>
                {errorMessage && (
                  <div className="bg-danger-light px-15px py-10px radius-8px d-flex gap-6 align-items-center">
                    <Warning />
                    <div className="fs-13 fw-400 text-black lh-sm">{errorMessage}</div>
                  </div>
                )}
                <div className="d-flex flex-column flex-lg-row gap-20">
                  {
                    ((!isImpersonating && !isAdmin) || (isCandidateCompleteReopened)) && (
                      <CustomButton
                      type="submit"
                      isLoading={Inprogress || uploadsInProgress}
                      contentClass="d-flex"
                      variant="primary"
                      disabled={!hasPoliceCheck || uploadsInProgress || showMessage || (isAdminCompleteOptionSelected && !isCandidateCompleteReopened)}
                      onClick={handleStartCheck}
                      className="btn-theme bg-teal text-black"
                    >
                      Start Checks <RightArrow color={"#000000"} />
                    </CustomButton>
                    )
                  }
                  
                </div>
              </>
            ) : userpersonaldetails?.referoo_status === "pending" ? (
              <div className="d-flex flex-column gap-25">
                {/* <div className="fs-16 fw-400 text-black">
                  The compliance check process has begun.
                </div>
                <div className="fs-16 fw-400 text-black">
                  You would have received an email from Referoo with a link to complete these checks. 
                  Please check your junk folder in case it was delivered there instead of your inbox.
                </div> */}                
                <div className="">Your unique link to access Referoo has been generated.</div>
                <div className="">Please click 'Open Referoo' below.</div>
                <div className="d-flex flex-column flex-lg-row gap-20 mt-3">            
                  <CustomButton
                    type="submit"
                    isLoading={Inprogress}
                    contentClass="d-flex"
                    variant="primary"                    
                    onClick={handleOpenReferooLink}
                    className="btn-theme bg-teal text-black"
                  >
                    Open Referoo <ExternalLinkIcon />
                  </CustomButton>
                </div>
              </div>
            ) : (userpersonaldetails?.referoo_status === "in_progress") ? (
              <div className="text-teal-dark">
                Stage 2: The compliance check is currently in progress. You will receive
                an update once it is completed.
              </div>
            ) : (userpersonaldetails?.referoo_status === "complete") ? (
              <div className="text-black fs-16">
                Stage 2: Compliance Checks has been completed and is currently in review by our 
                consultants. It will take up to 1 business day for a manual review to be completed. Thank 
                you for your patience.
              </div>
            ) : "No data available" }
          </div>
        </div>
      
    );
}

export default ComplainceCheck;